import type { ReactNode } from "react";
import { Pencil } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { formatPackSize } from "@/lib/materials/pack-size";
import { formatMoney } from "@/lib/materials/conversion-calc";
import { useMaterialConversions } from "@/hooks/useMaterialConversions";
import type { Material } from "@/types/material";

interface OverviewTabProps {
  material: Material;
  canEdit: boolean;
  onEdit: () => void;
  /** Switches to the "UoM & pack size" tab, where supplier codes are entered. */
  onOpenUomTab?: () => void;
}

/**
 * The material's existing key fields, read-only. Editing stays on
 * `CreateMaterialDialog` (opened here in edit mode) rather than duplicating
 * that form inline — this tab is a summary, not a second editor.
 */
export function OverviewTab({ material, canEdit, onEdit, onOpenUomTab }: OverviewTabProps) {
  const { t } = useLingui();
  const { data: supplierItems } = useMaterialConversions(material.id);
  // The ingredient's own supplier first, then any other its supplier items
  // (the UoM & pack size tab) are bought from, so a supplier entered on
  // either screen shows here (T-1412).
  const suppliers = [
    ...new Set(
      [material.supplier?.name ?? material.supplierName, ...(supplierItems ?? []).map((item) => item.supplier?.name)]
        .map((name) => name?.trim())
        .filter((name): name is string => !!name),
    ),
  ];
  // The SKU is the supplier's code, kept per supplier item on the UoM & pack
  // size tab. `material.sku` is only what older records carry.
  const skus = [
    ...new Set(
      (supplierItems ?? []).map((item) => item.supplierCode?.trim()).filter((code): code is string => !!code),
    ),
  ];
  if (skus.length === 0 && material.sku?.trim()) skus.push(material.sku.trim());

  return (
    <div className="flex flex-col items-start w-full">
      <div className="flex flex-col gap-6 px-6 w-full">
        <div className="flex flex-col gap-5 pb-px">
          <div className="flex gap-4 items-start">
            <div className="flex-1 flex flex-col gap-1 min-w-0">
              <p className="text-2xl font-medium leading-[28px] text-(--color-text-primary)">
                {t`Overview`}
              </p>
              <p className="text-sm text-(--color-text-secondary)">
                {t`Basic information about this ingredient.`}
              </p>
            </div>
            {canEdit && (
              <Button variant="brand-outline" onClick={onEdit} className="shrink-0">
                <Pencil className="size-4" />
                {t`Edit`}
              </Button>
            )}
          </div>
          <div className="h-px bg-(--color-surface-muted)" />
        </div>

        <div className="flex flex-col gap-5">
          <FormRow label={t`Name`}>
            <p className="text-base leading-[24px] text-(--color-text-primary)">{material.name}</p>
          </FormRow>
          <div className="h-px bg-(--color-surface-muted)" />

          <FormRow label={t`Type`}>
            <p className="text-base leading-[24px] text-(--color-text-primary)">
              {material.type === "COMPOUND"
                ? t`Compound`
                : material.type === "READY_TO_USE"
                  ? t`Ready to use`
                  : t`Raw`}
            </p>
          </FormRow>
          <div className="h-px bg-(--color-surface-muted)" />

          <FormRow label={t`Concept`}>
            <p className="text-base leading-[24px] text-(--color-text-primary)">
              {material.company?.name ?? t`Shared across concepts`}
            </p>
          </FormRow>
          <div className="h-px bg-(--color-surface-muted)" />

          <FormRow label={t`Purchase unit`} sublabel={t`How this ingredient is bought.`}>
            <p className="text-base leading-[24px] text-(--color-text-primary)">
              {formatPackSize(material)}
            </p>
          </FormRow>
          <div className="h-px bg-(--color-surface-muted)" />

          <FormRow label={t`Recipe unit`} sublabel={t`What recipe lines measure this in.`}>
            <p className="text-base leading-[24px] text-(--color-text-primary)">
              {material.recipeUom ?? material.unitOfMeasure}
            </p>
          </FormRow>
          <div className="h-px bg-(--color-surface-muted)" />

          <FormRow label={t`Supplier`}>
            <p className="text-base leading-[24px] text-(--color-text-primary)">
              {suppliers.length > 0 ? suppliers.join(", ") : "—"}
            </p>
          </FormRow>
          <div className="h-px bg-(--color-surface-muted)" />

          <FormRow label={t`Unit cost`}>
            <p className="text-base leading-[24px] text-(--color-text-primary)">
              {material.effectiveUnitCost != null
                ? `${formatMoney(material.effectiveUnitCost.value)} / ${material.effectiveUnitCost.unit}`
                : "—"}
            </p>
          </FormRow>
          <div className="h-px bg-(--color-surface-muted)" />

          <FormRow label={t`SKU`}>
            <p className="text-base leading-[24px] text-(--color-text-primary)">
              {skus.length > 0 ? skus.join(", ") : "—"}
              {onOpenUomTab ? (
                <>
                  {" "}
                  <button
                    type="button"
                    onClick={onOpenUomTab}
                    className="text-sm font-medium text-(--color-action) underline underline-offset-2"
                  >
                    {skus.length > 0 ? t`View on UoM & pack size` : canEdit ? t`Add on UoM & pack size` : t`View UoM & pack size`}
                  </button>
                </>
              ) : null}
            </p>
          </FormRow>
          <div className="h-px bg-(--color-surface-muted)" />

          <FormRow label={t`Description`}>
            <p className="text-base leading-[24px] text-(--color-text-primary) w-full max-w-[600px]">
              {material.description || "—"}
            </p>
          </FormRow>
        </div>
      </div>
    </div>
  );
}

function FormRow({ label, sublabel, children }: { label: string; sublabel?: string; children: ReactNode }) {
  return (
    <div className="flex gap-8 items-start">
      <div className="flex flex-col gap-0 w-[340px] shrink-0">
        <p className="text-sm font-medium leading-[20px] text-(--color-text-primary)">{label}</p>
        {sublabel && <p className="text-sm text-(--color-text-secondary) leading-[20px]">{sublabel}</p>}
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}
